import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Trash2, Star, ArrowUp, ArrowDown, Plus, Save, ExternalLink } from "lucide-react";

const CATS = ["Model", "Actor", "Actress", "Model & Actor", "Influencer", "Dancer", "Singer", "Creator", "Kid Artist", "Fresh Face", "Other"];

const FIELDS = [
  ["full_name", "Full Name"],
  ["display_name", "Professional Name"],
  ["slug", "URL slug"],
  ["city", "City"],
  ["country", "Country"],
  ["age", "Age"],
  ["height", "Height"],
  ["chest", "Chest"],
  ["waist", "Waist"],
  ["hips", "Hips"],
  ["shoe_size", "Shoe Size"],
  ["hair", "Hair"],
  ["eyes", "Eyes"],
  ["experience_years", "Experience (yrs)"],
  ["instagram", "Instagram"],
  ["youtube", "YouTube"],
  ["portfolio_url", "Portfolio URL"],
  ["languages", "Languages"],
  ["skills", "Skills (comma separated)"],
  ["availability", "Availability"],
];

export default function AdminEditProfileModal({ profile, onClose, onSaved }) {
  const [d, setD] = useState({});
  const [top, setTop] = useState({});
  const [busy, setBusy] = useState(false);
  const [vTitle, setVTitle] = useState("");
  const [vUrl, setVUrl] = useState("");
  const uid = profile.user_id;

  useEffect(() => {
    setD({ ...(profile.data || {}) });
    setTop({ full_name: profile.full_name, city: profile.city, featured: !!profile.featured, status: profile.status });
  }, [profile.user_id]);

  const setDF = (k, v) => setD((x) => ({ ...x, [k]: v }));

  const saveText = async () => {
    setBusy(true);
    try {
      const patch = { ...d };
      // Normalize skills / languages if string
      if (typeof patch.skills === "string") {
        patch.skills = patch.skills.split(",").map((s) => s.trim()).filter(Boolean);
      }
      // patch data fields
      const dataPatch = Object.fromEntries(Object.entries(patch).filter(([k]) => k !== "gallery" && k !== "videos" && k !== "profile_photo"));
      await api.post("/admin/talents/update", { user_id: uid, patch: dataPatch });
      // patch top-level
      if (top.full_name !== profile.full_name || top.city !== profile.city) {
        await api.post("/admin/talents/update", { user_id: uid, patch: { full_name: top.full_name, city: top.city } });
      }
      if (top.featured !== profile.featured) {
        await api.post("/admin/featured", { user_id: uid, featured: !!top.featured });
      }
      if (top.status !== profile.status) {
        await api.post("/admin/verify", { user_id: uid, status: top.status });
      }
      toast.success("Profile saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const uploadPhotos = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = await Promise.all(files.map(async (fl) => {
        const fd = new FormData(); fd.append("file", fl);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return data.url;
      }));
      const newGallery = [...(d.gallery || []), ...urls];
      await api.post("/admin/talents/photos/reorder", { user_id: uid, gallery: newGallery });
      if (!d.profile_photo && newGallery.length) {
        await api.post("/admin/talents/photos/set-cover", { user_id: uid, photo_url: newGallery[0] });
        setDF("profile_photo", newGallery[0]);
      }
      setDF("gallery", newGallery);
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added`);
      onSaved?.();
    } catch (err) { toast.error("Upload failed"); }
    setBusy(false); e.target.value = "";
  };

  const removePhoto = async (url) => {
    if (!window.confirm("Remove this photo?")) return;
    const r = await api.post("/admin/talents/photos/remove", { user_id: uid, photo_url: url });
    setDF("gallery", r.data.gallery);
    if (r.data.profile_photo !== undefined) setDF("profile_photo", r.data.profile_photo);
    toast.success("Photo removed"); onSaved?.();
  };

  const setCover = async (url) => {
    await api.post("/admin/talents/photos/set-cover", { user_id: uid, photo_url: url });
    setDF("profile_photo", url);
    toast.success("Hero photo updated"); onSaved?.();
  };

  const move = async (i, dir) => {
    const arr = [...(d.gallery || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setDF("gallery", arr);
    await api.post("/admin/talents/photos/reorder", { user_id: uid, gallery: arr });
    onSaved?.();
  };

  const addVideo = async () => {
    if (!vUrl.trim()) return;
    try {
      await api.post("/admin/talents/videos/add", { user_id: uid, title: vTitle || "Video", url: vUrl.trim() });
      setDF("videos", [...(d.videos || []), { title: vTitle || "Video", url: vUrl.trim(), category: "Music Video" }]);
      setVTitle(""); setVUrl("");
      toast.success("Video added"); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add video"); }
  };

  const removeVideo = async (url) => {
    const r = await api.post("/admin/talents/videos/remove", { user_id: uid, url });
    setDF("videos", r.data.videos);
    toast.success("Video removed"); onSaved?.();
  };

  const gallery = d.gallery || [];
  const videos = d.videos || [];

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="admin-edit-modal">
      <div className="sv-card w-full max-w-5xl p-6 lg:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#8A8478] hover:text-[#F4E4BC]" data-testid="edit-close"><X size={20} /></button>
        <div className="sv-overline mb-2">Edit Profile</div>
        <div className="font-serif-hero text-3xl text-[#F4E4BC] mb-1">{profile.full_name}</div>
        <div className="text-xs text-[#8A8478] mb-6">{uid} · slug: {d.slug || "—"}</div>

        {/* Top-level status controls */}
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <input placeholder="Full name" className="sv-input" value={top.full_name || ""} onChange={(e) => setTop({ ...top, full_name: e.target.value })} />
          <input placeholder="City (top)" className="sv-input" value={top.city || ""} onChange={(e) => setTop({ ...top, city: e.target.value })} />
          <select className="sv-select" value={top.status || "verified"} onChange={(e) => setTop({ ...top, status: e.target.value })}>
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified (Live)</option>
            <option value="needs_changes">Needs Changes</option>
            <option value="rejected">Rejected</option>
          </select>
          <label className="text-xs text-[#CDC7BA] flex items-center gap-2 sv-input justify-center cursor-pointer">
            <input type="checkbox" checked={!!top.featured} onChange={(e) => setTop({ ...top, featured: e.target.checked })} /> Featured on Home
          </label>
        </div>

        {/* Data fields */}
        <div className="grid md:grid-cols-3 gap-3">
          <select className="sv-select" value={d.categories?.[0] || ""} onChange={(e) => setDF("categories", [e.target.value])}>
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="sv-select" value={d.gender || ""} onChange={(e) => setDF("gender", e.target.value)}>
            <option value="">— gender —</option>
            <option value="female">Female → /women/…</option>
            <option value="male">Male → /men/…</option>
            <option value="non-binary">Non-binary</option>
          </select>
          <input placeholder="slug (e.g. visakha)" className="sv-input" value={d.slug || ""} onChange={(e) => setDF("slug", e.target.value)} />
          {FIELDS.filter(([k]) => !["slug"].includes(k)).map(([k, l]) => (
            <input key={k} placeholder={l} className="sv-input" value={
              Array.isArray(d[k]) ? d[k].join(", ") : (d[k] || "")
            } onChange={(e) => setDF(k, e.target.value)} />
          ))}
          <textarea placeholder="About / Bio" rows={3} className="sv-input md:col-span-3" value={d.about || ""} onChange={(e) => setDF("about", e.target.value)} />
        </div>

        <div className="flex justify-end mt-4">
          <button onClick={saveText} disabled={busy} className="sv-btn-primary inline-flex items-center gap-2" data-testid="edit-save-text"><Save size={14} />{busy ? "Saving…" : "Save Details"}</button>
        </div>

        {/* Photos */}
        <div className="mt-8 pt-8 border-t border-[rgba(255,255,255,0.06)]">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono-label text-[10px] text-[#C5A059] tracking-[0.32em]">PORTFOLIO PHOTOS · {gallery.length}</div>
            <label className="sv-btn-outline text-xs inline-flex items-center gap-2 cursor-pointer" data-testid="edit-upload-photos">
              <Upload size={12} />Upload HD Photos
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={uploadPhotos} />
            </label>
          </div>
          {gallery.length === 0 && <div className="text-xs text-[#8A8478]">No photos yet. Upload multiple HD images — the first uploaded becomes the hero photo automatically.</div>}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {gallery.map((g, i) => (
              <div key={g + i} className="relative group border border-[rgba(255,255,255,0.06)] rounded-lg overflow-hidden">
                <img src={g} className="w-full aspect-[3/4] object-cover" alt="" />
                {d.profile_photo === g && (
                  <div className="absolute top-1 left-1 bg-[#C5A059] text-[#0A0A0B] text-[8px] font-mono-label px-1.5 py-0.5 tracking-[0.2em]">HERO</div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col justify-between p-2">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setCover(g)} title="Make Hero" className="p-1 bg-[#131316]/80 rounded hover:bg-[#C5A059] hover:text-[#0A0A0B]" data-testid={`photo-hero-${i}`}><Star size={12} /></button>
                    <button onClick={() => removePhoto(g)} title="Delete" className="p-1 bg-[#131316]/80 rounded hover:bg-red-500 text-red-400 hover:text-white" data-testid={`photo-del-${i}`}><Trash2 size={12} /></button>
                  </div>
                  <div className="flex justify-between items-end">
                    <div className="text-[9px] font-mono-label text-[#F4E4BC]">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex gap-1">
                      <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="p-1 bg-[#131316]/80 rounded disabled:opacity-30 hover:bg-[#C5A059] hover:text-[#0A0A0B]" data-testid={`photo-up-${i}`}><ArrowUp size={11} /></button>
                      <button onClick={() => move(i, 1)} disabled={i === gallery.length - 1} title="Move down" className="p-1 bg-[#131316]/80 rounded disabled:opacity-30 hover:bg-[#C5A059] hover:text-[#0A0A0B]" data-testid={`photo-down-${i}`}><ArrowDown size={11} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Videos */}
        <div className="mt-8 pt-8 border-t border-[rgba(255,255,255,0.06)]">
          <div className="font-mono-label text-[10px] text-[#C5A059] tracking-[0.32em] mb-3">VIDEOS · {videos.length}</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr,2fr,auto] gap-2">
            <input placeholder="Title (e.g. Suniyan Suniyan)" className="sv-input" value={vTitle} onChange={(e) => setVTitle(e.target.value)} data-testid="edit-video-title" />
            <input placeholder="YouTube / Instagram / Vimeo URL (https://…)" className="sv-input" value={vUrl} onChange={(e) => setVUrl(e.target.value)} data-testid="edit-video-url" />
            <button onClick={addVideo} className="sv-btn-outline text-xs inline-flex items-center gap-1" data-testid="edit-add-video"><Plus size={12} />Add</button>
          </div>
          {videos.length > 0 && (
            <ul className="mt-3 space-y-2">
              {videos.map((v, i) => {
                const url = typeof v === "string" ? v : v.url;
                const title = typeof v === "string" ? "Video" : (v.title || "Video");
                return (
                  <li key={url + i} className="flex items-center justify-between text-xs text-[#CDC7BA] border border-[rgba(255,255,255,0.06)] rounded-lg px-3 py-2">
                    <span className="truncate">
                      <strong className="text-[#F4E4BC]">{title}</strong> — <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] inline-flex items-center gap-1">{url.slice(0, 60)}{url.length > 60 ? "…" : ""} <ExternalLink size={10} /></a>
                    </span>
                    <button onClick={() => removeVideo(url)} className="text-[#8A8478] hover:text-red-400 ml-2" data-testid={`edit-rm-video-${i}`}><X size={14} /></button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="sv-btn-outline text-xs">Close</button>
          <button onClick={saveText} disabled={busy} className="sv-btn-primary inline-flex items-center gap-2"><Save size={14} />Save & Publish</button>
        </div>
      </div>
    </div>
  );
}
